import React, { useState } from 'react';
import { observer, useLocalStore } from 'mobx-react-lite';
import useStore from '../../services/hooks/useStore';
import { Modal, ModalHeader, ModalBody, Footer } from './styles';
import Botao from '../Botoes/Botao';
import { TextField } from '@material-ui/core';
import { apiV2 } from '../../services/utils';
import X9Logger from '../../services/X9Logger';
import { InfNFe } from '../../@types/jsonContent';

type Props = {
  open: boolean;
  handleClose: () => void;
  movimentacaoUuid: string;
  formaPagamento: string;
  indicadorPagamento: string;
  notaFiscalId?: string;
  jsonContent?: InfNFe;
  informacoesComplementaresInicial?: string;
  onSuccess: () => void;
};



const ModalEmitirNFe: React.FC<Props> = ({
  open,
  handleClose,
  movimentacaoUuid,
  formaPagamento,
  indicadorPagamento,
  notaFiscalId,
  jsonContent,
  informacoesComplementaresInicial = '',
  onSuccess
}) => {
  const store = useStore();
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string>('');

  const debugLog = (...args: any[]) => {
    console.log('[ModalEmitirNFe]', ...args);
  };

  function normalizeInformacoesComplementaresForInput(value: string): string {
    return (value || '').replace(/\\n/g, ' ').replace(/[\r\n]+/g, ' ');
  }

  function sanitizeInformacoesComplementaresForEmit(value: string): string {
    return normalizeInformacoesComplementaresForInput(value).replace(/\s{2,}/g, ' ').trim();
  }

  const state = useLocalStore(() => ({
    informacoesComplementares: sanitizeInformacoesComplementaresForEmit(informacoesComplementaresInicial),
  }));

  async function emitirNFe() {
    debugLog('emitirNFe iniciado', {
      movimentacaoUuid,
      formaPagamento,
      indicadorPagamento,
      notaFiscalId,
      hasJsonContent: Boolean(jsonContent),
    });
    const informacoesComplementaresSanitizadas = sanitizeInformacoesComplementaresForEmit(state.informacoesComplementares);

    if (informacoesComplementaresSanitizadas.length > 200) {
      setErro('Informações complementares não podem exceder 200 caracteres.');
      debugLog('emitirNFe abortado: informacoesComplementares > 200');
      return;
    }

    setLoading(true);
    setErro('');
    try {
      let body: string;
      let headers: { 'Content-Type': string };

      // Se recebeu dados da pré-emissão (fluxo com revisão de dados)
      if (notaFiscalId && jsonContent) {
        debugLog('Fluxo com pre-emissao (notaFiscalId + jsonContent)');
        const updatedJsonContent = JSON.parse(JSON.stringify(jsonContent));

        if (informacoesComplementaresSanitizadas && updatedJsonContent.infNFe) {
          if (!updatedJsonContent.infNFe.infAdic) {
            updatedJsonContent.infNFe.infAdic = {};
          }
          updatedJsonContent.infNFe.infAdic.infCpl = informacoesComplementaresSanitizadas;
        }

        const emitParams = new URLSearchParams();
        emitParams.set('origem_nota', 'movimentacao');
        emitParams.set('movimentacao_id', movimentacaoUuid);
        emitParams.set('nota_fiscal_id', notaFiscalId);
        emitParams.set('json_content', JSON.stringify(updatedJsonContent));
        if (informacoesComplementaresSanitizadas) {
          emitParams.set('complemento', informacoesComplementaresSanitizadas);
        }
        body = emitParams.toString();
        headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
      } else {
        debugLog('Fluxo legado sem pre-emissao');
        // Fluxo legado: sem nota_fiscal_id/json_content no Swagger atual — apenas campos documentados (pode falhar até haver pré-emissão)
        const emitParams = new URLSearchParams();
        emitParams.set('origem_nota', 'movimentacao');
        emitParams.set('movimentacao_id', movimentacaoUuid);
        if (informacoesComplementaresSanitizadas) {
          emitParams.set('complemento', informacoesComplementaresSanitizadas);
        }
        body = emitParams.toString();
        headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
      }

      debugLog('Payload emissao montado', body);
      debugLog('POST /nfe/emitir iniciado');
      await apiV2.post(`/nfe/emitir/?prop=${store.propriedade?.id}`, body, { headers });
      debugLog('POST /nfe/emitir sucesso');

      store.notificar('NFe emitida com sucesso!');
      onSuccess();
      handleClose();
      state.informacoesComplementares = '';
    } catch (error: any) {
      debugLog('Erro em emitirNFe', error?.response?.data || error);
      X9Logger.report(error as Error);
      if (error.response?.data?.erros) {
        setErro(Array.isArray(error.response.data.erros) ? error.response.data.erros.join(', ') : error.response.data.erros);
      } else {
        setErro('Erro ao emitir NFe. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal onClose={handleClose} open={open}>
      <ModalHeader>Emitir Nota Fiscal</ModalHeader>
      <ModalBody style={{ width: '500px' }}>
        <TextField
          label="Informações Complementares"
          value={state.informacoesComplementares}
          onChange={(e) => {
            const normalized = normalizeInformacoesComplementaresForInput(e.target.value);
            state.informacoesComplementares = normalized.slice(0, 200);
            setErro('');
          }}
          multiline
          rows={4}
          fullWidth
          helperText={`${state.informacoesComplementares.length}/200 caracteres${erro ? ` - ${erro}` : ''}`}
          error={!!erro}
          inputProps={{ maxLength: 200 }}
        />
      </ModalBody>
      <Footer>
        <Botao onClick={handleClose} variant="contained" cor="gray" disabled={loading}>
          Cancelar
        </Botao>
        <Botao onClick={emitirNFe} variant="contained" cor="#00C853" disabled={loading}>
          {loading ? 'Emitindo...' : 'Emitir'}
        </Botao>
      </Footer>
    </Modal>
  );
};

export default observer(ModalEmitirNFe);

